<template>
  <div
    v-if="!!phaseData && !loading && pharmaceuticalDomainConfig.CUSTOMSUMMARIZEDFLOW !== ECustomSummmarizedflow.SANOFI"
  >
    <div class="mb-8">
      <span class="text-subtitle-1 font-weight-medium grey--text text--darken-4">Hipótese diagnóstica</span>
      <text-area id="hypothesis" :value="attendanceHypothesis" auto-grow readonly hide-details />
    </div>
    <div>
      <span class="font-weight-medium font-size-16 title-text">Altura e peso do paciente</span>
      <div class="d-flex mt-2">
        <v-text-field
          label="Altura (cm)"
          v-mask="['###']"
          v-model="phaseData.heightCm"
          @blur="updatePatientHeight"
          filled
          class="mr-6"
          type="number"
          clearable
        />
        <v-text-field
          label="Peso (kg)"
          v-model="phaseData.weightKg"
          @blur="updatePatientWeight"
          filled
          type="number"
          clearable
        />
      </div>
    </div>
    <div v-if="currentMedicines.length > 0" class="mb-8">
      <div class="text-subtitle-1 font-weight-medium grey--text text--darken-4">Medicamentos em uso</div>
      <div
        class="mt-2 grey--text text--darken-4 text-body-2"
        v-for="(medicine, index) in currentMedicines"
        :key="`medicine${index}`"
      >
        {{ medicine }}
      </div>
    </div>
    <v-sheet class="mb-8" v-else>
      <span class="mt-2 black--text text-body-2">Nenhum medicamento em uso.</span>
    </v-sheet>

    <template v-if="error">
      <h3>Um erro ocorreu ao tentar carregar os medicamentos!</h3>
      <v-btn class="flex-grow-0" large color="primary" @click="loadData" :loading="loading"
        >tentar novamente
        <v-icon class="ml-2" large>mdi-refresh</v-icon>
      </v-btn>
    </template>

    <div v-else>
      <p
        v-if="asasNotPositive && attendance.disease.id === diseases.ESPONDILOARTRITIS"
        class="my-2 text-subtitle-1 grey--text text--darken-4"
      >
        Essa inteligência foi criada utilizando critérios classificatórios e guidelines de diagnóstico e tratamento
        focadas em espondiloartrites. Entretanto, alguns casos podem não se enquadrar nesses critérios ou ainda
        apresentar um diagnóstico alternativo. Sugerimos, nesse caso, a discussão do caso com um médico referência no
        tema. Clique no menu à direita em
        <v-icon color="primary">mdi-human-greeting-proximity</v-icon>
        (INTERCONSULTA) para discutir o seu caso com um EXPERT.
      </p>
      <div>
        <div class="d-flex align-center justify-space-between mb-4">
          <span class="text-subtitle-1 font-weight-medium grey--text text--darken-4">Medicamentos</span>
          <v-btn-toggle v-model="showAllTreatments" rounded dense @change="$emit('btn:toggle', showAllTreatments)">
            <v-btn
              :value="false"
              depressed
              :class="['text-none', { 'bg-active': !showAllTreatments, 'bg-inactive': showAllTreatments }]"
            >
              <v-icon size="18" class="pa-2" v-if="!showAllTreatments">mdi-check</v-icon>
              Ver indicados
            </v-btn>
            <v-btn
              :value="true"
              depressed
              :class="['text-none', { 'bg-active': showAllTreatments, 'bg-inactive': !showAllTreatments }]"
            >
              <v-icon size="18" class="pa-2" v-if="showAllTreatments">mdi-check</v-icon>
              Ver todos
            </v-btn>
          </v-btn-toggle>
        </div>
        <v-text-field
          label="Buscar medicamento"
          outlined
          prepend-icon="mdi-magnify"
          @click="showAllTreatments = true"
          @blur="onBlurFilterTreatmentList($event)"
          @input="filterTreatmentList"
        ></v-text-field>
      </div>

      <v-expansion-panels v-model="panels" multiple>
        <v-expansion-panel class="mb-4" v-for="(treatment, idx) in treatmentList" :key="treatment.id">
          <v-expansion-panel-header class="header rounded" title="Receitar">
            <template v-slot:actions>
              <span class="text-subtitle-2" style="color: #1976d2">
                {{ panels.includes(idx) ? 'Ocultar descrição' : 'Ver descrição' }}</span
              >
            </template>
            <span class="text-subtitle-1">
              <v-btn icon @click="phaseData.selectedTreatments.push(treatment)" class="mr-3" color="primary">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
              {{ treatment.medicine.name }}
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="content">
            {{ treatment.medicine.description }}
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <div>
      <span class="text-h6 mt-4">Novos medicamentos receitados</span>
      <v-expansion-panels multiple>
        <v-expansion-panel class="mb-4" v-for="(treatment, idx) in phaseData.selectedTreatments" :key="treatment.id">
          <v-expansion-panel-header class="header rounded" title="Retirar da receita" hide-actions>
            <span class="text-subtitle-1">
              <v-btn icon @click="phaseData.selectedTreatments.splice(idx, 1)" class="mr-3" color="primary">
                <v-icon>mdi-close</v-icon>
              </v-btn>
              {{ treatment.medicine.name }}
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="d-flex align-center flex-wrap">
              <v-autocomplete
                label="Selecionar prescrição"
                outlined
                returnObject
                menu-props="closeOnContentClick"
                :items="getTreatmentDosagesOptions(treatment.dosages)"
                v-model="treatment.selectedDosage"
              ></v-autocomplete>
            </div>

            <div v-if="treatment.selectedDosage" class="mb-4">
              <div>Posologia:</div>
              <span
                class="text-pre-line"
                v-if="dosageDisplay[treatment.id] || treatment.selectedDosage.value?.length <= textLimit"
              >
                {{ treatment.selectedDosage.value }}
              </span>
              <span v-else class="text-pre-line"> {{ treatment.selectedDosage.value?.slice(0, textLimit) }}... </span>
              <a
                v-if="treatment.selectedDosage.value?.length >= textLimit"
                @click="toggleDosage(treatment.id)"
                class="ml-1 text-decoration-underline"
              >
                {{ dosageDisplay[treatment.id] ? 'Esconder' : 'Ver mais' }}
              </a>
            </div>

            <div v-if="treatment.guidelines" class="mb-4">
              <div>Orientações:</div>
              <span
                class="text-pre-line"
                v-if="guidelineDisplay[treatment.id] || treatment.guidelines.length <= textLimit"
              >
                {{ treatment.guidelines }}
              </span>
              <span v-else class="text-pre-line">{{ treatment.guidelines.slice(0, textLimit) }}... </span>
              <a
                v-if="treatment.guidelines.length >= textLimit"
                @click="toggleGuideline(treatment.id)"
                class="ml-1 text-decoration-underline"
              >
                {{ guidelineDisplay[treatment.id] ? 'Esconder' : 'Ver mais' }}
              </a>
            </div>

            <div>
              <div>Informações:</div>
              <span
                class="text-pre-line"
                v-if="informationDisplay[treatment.id] || treatment.medicine.description.length <= textLimit"
              >
                {{ treatment.medicine.description }}
              </span>
              <span v-else class="text-pre-line">{{ treatment.medicine.description.slice(0, textLimit) }}... </span>
              <a
                v-if="treatment.medicine.description.length >= textLimit"
                @click="toggleInformation(treatment.id)"
                class="ml-1 text-decoration-underline"
              >
                {{ informationDisplay[treatment.id] ? 'Esconder' : 'Ver mais' }}
              </a>
            </div>

            <div class="d-flex gap-2 ml-auto mt-4" :class="{ 'mr-auto mt-2': $vuetify.breakpoint.smAndDown }">
              <v-btn
                class="text-none"
                text
                color="primary"
                :disabled="!treatment.selectedDosage"
                :loading="loading"
                @click="openDialog('printingTemplate', { treatment })"
              >
                <v-icon left>mdi-clipboard-edit-outline</v-icon>
                Imprimir Receita
              </v-btn>
              <v-btn
                text
                class="text-none"
                color="primary"
                @click="openPrintExamPrescriptionModal(treatment)"
                :disabled="treatment.prescriptions.length === 0"
              >
                <v-icon left>mdi-flask-empty-outline</v-icon>
                Imprimir prescrição de exames
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
    <v-dialog v-model="examModal" v-if="!!currentTreatment" max-width="600px">
      <v-card class="pb-4">
        <v-card-title class="justify-center"
          >Impressão de exames para {{ currentTreatment.medicine.name }}
        </v-card-title>

        <field-label
          v-if="examPrescriptionTemplates.length > 0"
          label="Modelo de impressão dos exames"
          titleClass="text-body-1"
          titleColorClass="black--text text--lighten-1"
          class="px-8"
        >
          <select-field
            :items="examPrescriptionTemplates"
            v-model="selectedExamPrescriptionTemplateId"
            itemValue="id"
            itemText="name"
            itemid="id"
            :disabled="examPrescriptionTemplates.length === 1"
          />
        </field-label>

        <div
          v-for="exam in currentTreatmentExams"
          :key="exam.id"
          class="d-flex align-center justify-space-between mx-12"
        >
          <span class="text-body-1 black--text">{{ exam.name }}</span>

          <download-frame :url="printExamPrescriptionUrl(exam.id)" remoteUrl>
            <template #default="{ loading }">
              <v-btn icon large color="primary" :loading="loading">
                <v-icon>mdi-printer</v-icon>
              </v-btn>
            </template>
          </download-frame>
        </div>
      </v-card>
    </v-dialog>

    <select-printing-template-dialog
      v-if="dialog.printingTemplate.show"
      v-model="dialog.printingTemplate.show"
      type="receipt"
      v-bind="dialog.printingTemplate.data"
      @closed="
        (selectedTemplate) => {
          saveMedicalDocument(dialog.printingTemplate.treatment, selectedTemplate)
          dialog.printingTemplate = { show: false, data: {}, treatment: null }
        }
      "
    />
  </div>
  <div
    v-else-if="
      !!phaseData &&
      attendance.disease.id === diseases.ATOPIC_DERMATITIS &&
      pharmaceuticalDomainConfig.CUSTOMSUMMARIZEDFLOW === ECustomSummmarizedflow.SANOFI
    "
  >
    <field-label :label="'Qual o tratamento proposto?'" required>
      <v-row>
        <v-col v-for="option in medicineOptions" :key="option.value" lg="4" cols="6">
          <v-checkbox
            class="mt-4"
            :label="option.text"
            v-model="phaseData.medicines.selected"
            :value="option.value"
            hide-details
          />
        </v-col>
        <v-col cols="12">
          <text-field
            label="Qual?"
            v-if="phaseData.medicines.selected.includes('other')"
            value="other"
            :required="phaseData.medicines.selected.includes('other')"
            v-model="phaseData.medicines.other"
          />
        </v-col>
      </v-row>
    </field-label>
  </div>
</template>
<script>
import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'

import { ECustomSummmarizedflow } from '@/constants/customSummarizedFlow'
import { createNamespacedHelpers } from 'vuex'

const {
  mapGetters: attendanceGettersMap,
  mapActions: attendanceActionsMap,
  mapState: attendanceStateMap,
} = createNamespacedHelpers('attendance')

const { mapGetters: authGettersMap } = createNamespacedHelpers('auth')

import DownloadFrame from '@/components/DownloadFrame.vue'
import FieldLabel from '@/components/FieldLabel.vue'
import SelectField from '@/components/SelectField.vue'
import TextField from '@/components/TextField.vue'
import { DISEASES } from '@/constants/disease'
import { MEDICINE_PERIODS } from '@/constants/medicine'
import { TREATMENT_EXHIBITION_RULES } from '@/store/attendance/config/treatmentExhibitionConfig'
import TextArea from '@/components/TextArea.vue'
import { mask } from 'vue-the-mask'
import SelectPrintingTemplateDialog from '@/components/PrintingTemplate/SelectPrintingTemplateDialog.vue'

export default {
  components: { TextArea, FieldLabel, SelectField, DownloadFrame, TextField, SelectPrintingTemplateDialog },
  extends: BasePhase,
  computed: {
    ...attendanceStateMap(['isServerSyncedData', 'isLocalSyncedData', 'isSavingData']),
    ...attendanceGettersMap([
      'getPhaseDataByKey',
      'attendanceDiseaseId',
      'attendance',
      'attendancePatient',
      'attendanceHypothesis',
    ]),
    ...authGettersMap(['pharmaceuticalDomainConfig']),
    textLimit() {
      return 165
    },
    ECustomSummmarizedflow() {
      return ECustomSummmarizedflow
    },
    stepsPhaseData() {
      return this.getPhaseDataByKey('steps')
    },
    showTemplateSelect() {
      return (
        this.medicinePrescriptionTemplates.length > 0 &&
        (this.phaseData.selectedTreatments.length > 0 || this.phaseData.customPrescriptions?.length > 0)
      )
    },
    medicineHistory() {
      if (this.getPhaseDataByKey('dados_especificos')) {
        return Object.values(this.getPhaseDataByKey('dados_especificos')?.medicines || {}) || []
      } else {
        return Object.values(this.getPhaseDataByKey('historico_medicamentoso')?.medicines || {}) || []
      }
    },
    currentMedicines() {
      const currentMedicinesStringArr = this.medicineHistory.map((m) => {
        let period = m.currentPeriod
        if (period?.dosage) {
          if (period.startFormatted) {
            return `${m.medicine.name} desde ${period.startFormatted} com dosagem máxima de ${
              period.dosage
            }mg ${MEDICINE_PERIODS[period.period].replace('(n)', period.length)}.`
          } else {
            return `${m.medicine.name} com dosagem máxima de ${period.dosage}mg ${MEDICINE_PERIODS[
              period.period
            ].replace('(n)', period.length)}.`
          }
        } else {
          return period && period.startFormatted ? `${m.medicine.name} desde ${period.startFormatted}` : m.medicine.name
        }
      })

      return currentMedicinesStringArr.sort((a, b) => a.localeCompare(b))
    },
    treatmentList() {
      return this.shownTreatments
        .filter((m) => !this.phaseData.selectedTreatments.some((s) => s.id === m.id))
        .sort((a, b) => a.medicine.name.localeCompare(b.medicine.name))
    },
    currentTreatmentExams() {
      return this.currentTreatment?.prescriptions
        ?.filter((p) => !this.currentTreatment.selectedDosage || p[this.currentTreatment.selectedDosage.key])
        .map((p) => ({ ...p, name: p.exams.map((e) => e.name).join(', ') }))
    },
    medicinePrescriptionTemplates() {
      return this.templates.map((t) => ({ id: t.id, name: t.name }))
    },
    examPrescriptionTemplates() {
      return this.templates.map((t) => ({ id: t.id, name: t.name }))
    },
    asasNotPositive() {
      return (
        this.attendance.disease.id === DISEASES.ESPONDILOARTRITIS &&
        this.attendance.summary?.asasPeriferico?.value !== '+' &&
        this.attendance.summary?.asasAxial?.value != '+'
      )
    },
    shownTreatments() {
      if (this.showAllTreatments) {
        return this.treatments.filter((treatment) => {
          const flowExclusion = treatment.exhibitionConfig?.flowExclusion || []
          if (!flowExclusion.includes('exclude_from_all_list_sanofi_flow')) {
            return true
          }
          return TREATMENT_EXHIBITION_RULES[treatment.disease.id].flowExclusion[
            'exclude_from_all_list_sanofi_flow'
          ].check(this.attendance)
        })
      }

      if (this.attendance.disease.id === this.diseases.ESPONDILOARTRITIS && this.asasNotPositive) {
        return []
      }

      return this.treatments.filter((t) => {
        const exclusion = t.exhibitionConfig?.exclusion || []
        const inclusion = t.exhibitionConfig?.inclusion || []
        return (
          !exclusion.some((configKey) =>
            TREATMENT_EXHIBITION_RULES[t.disease.id].exclusion[configKey].check(this.attendance),
          ) &&
          inclusion.some((configKey) =>
            TREATMENT_EXHIBITION_RULES[t.disease.id].inclusion[configKey].check(this.attendance),
          )
        )
      })
    },
  },
  async mounted() {
    if (!!this.phaseData) {
      this.phaseData.weightKg = this.attendancePatient.currentWeightKg
      this.phaseData.heightCm = this.attendancePatient.currentHeightCm
    }
    await this.loadData()
  },
  directives: {
    mask: {
      bind(el, binding, vnode, oldVnode) {
        if (binding.value) {
          mask(el, binding, vnode, oldVnode)
        }
      },
    },
  },
  data() {
    return {
      dialog: {
        printingTemplate: {
          show: false,
          data: {},
          treatment: null,
        },
      },
      panels: [],
      medicineOptions: [
        {
          text: 'Nenhum',
          value: 'none',
        },
        {
          text: 'Corticoide Tópico',
          value: 'corticoide_topico',
        },
        {
          text: 'Corticoide Injetável',
          value: 'corticoide_injetavel',
        },
        {
          text: 'Metotrexato',
          value: 'metotrexato',
        },
        {
          text: 'Ciclosporina',
          value: 'ciclosporina',
        },
        {
          text: 'Azatioprina',
          value: 'azatioprina',
        },
        {
          text: 'Micofenolato',
          value: 'micofenolato',
        },
        {
          text: 'Dupilumabe',
          value: 'dupilumabe',
        },
        {
          text: 'Fototerapia',
          value: 'fototerapia',
        },
        {
          text: 'Upadacitinibe',
          value: 'upadacitinibe',
        },
        {
          text: 'Baricitinibe',
          value: 'baricitinibe',
        },
        {
          text: 'Abrocitinibe',
          value: 'abrocitinibe',
        },
        {
          text: 'Outro',
          value: 'other',
        },
      ],
      diseases: DISEASES,
      buttons: [
        'bold',
        'italic',
        'underline',
        'strikethrough',
        'ul',
        'ol',
        'font',
        'fontsize',
        'paragraph',
        'lineHeight',
        'superscript',
        'subscript',
      ],
      informationDisplay: {},
      guidelineDisplay: {},
      descriptionDisplay: {},
      dosageDisplay: {},
      showAllTreatments: false,
      loading: false,
      error: false,
      treatments: [],
      treatmentsCopy: [],
      currentTreatment: null,
      examModal: false,
      templates: [],
      selectedMedicinePrescriptionTemplateId: null,
      selectedExamPrescriptionTemplateId: null,
    }
  },
  methods: {
    ...attendanceActionsMap(['startServerSyncing', 'endServerSyncing']),
    openDialog(dialog, data) {
      const dialogMap = {
        printingTemplate: ({ treatment }) => {
          this.dialog.printingTemplate = {
            show: true,
            data: {
              patientId: this.attendance.patient.id,
              text: `${treatment.medicine.name}: <br/> ${treatment.selectedDosage.value}`,
            },
            treatment,
          }
        },
      }

      dialogMap[dialog]?.(data)
    },
    async saveCustomPrescription() {
      if (this.isServerSyncedData || this.isSavingData) {
        return
      }
      try {
        await this.startServerSyncing()
        await this.$api.patchAttendance(this.attendance.id, this.attendance)
        await this.endServerSyncing(true)
      } catch (error) {
        await this.endServerSyncing(false)
        if (showError) {
          this.$root.error(error)
        } else {
          this.$logger.warn('Não foi possível salvar os dados do atendimento no servidor!', error)
        }
      }
      return Promise.resolve({})
    },
    toggleInformation(id) {
      this.$set(this.informationDisplay, id, !this.informationDisplay[id])
    },
    toggleGuideline(id) {
      this.$set(this.guidelineDisplay, id, !this.guidelineDisplay[id])
    },
    toggleDescription(id) {
      this.$set(this.descriptionDisplay, id, !this.descriptionDisplay[id])
    },
    toggleDosage(id) {
      this.$set(this.dosageDisplay, id, !this.dosageDisplay[id])
    },
    getRangeText(min, max, unit) {
      if (!min && !max) {
        return `sem restrições de ${unit === 'anos' ? 'idade' : 'peso'}`
      }
      if (!min) {
        return `até ${max} ${unit}`
      }
      if (!max) {
        return `a partir de ${min} ${unit}`
      }

      return `de ${min} a ${max} ${unit}`
    },
    printExamPrescriptionUrl(prescriptionId) {
      if (this.$util.apiURL()[this.$util.apiURL().length - 1] !== '/') {
        return `${this.$util.apiURL()}/treatment/exam/print/${prescriptionId}/${this.attendance.id}/${
          this.selectedExamPrescriptionTemplateId
        }`
      } else {
        return `${this.$util.apiURL()}treatment/exam/print/${prescriptionId}/${this.attendance.id}/${
          this.selectedExamPrescriptionTemplateId
        }`
      }
    },
    async saveMedicalDocument(treatment, template) {
      const data = {
        dosage: {
          medicine: treatment.medicine.name,
          posology: treatment.selectedDosage.value,
        },
      }
      try {
        await this.$api.medicalDocument.create({
          patientId: this.attendancePatient.id,
          kind: 'prescription',
          data,
          templateId: template.id,
          attendanceId: this.attendance.id,
        })
      } catch (e) {
        console.error(e)
      }
    },
    getPrintMedicinePrescriptionUrl(treatment) {
      if (this.$util.apiURL()[this.$util.apiURL().length - 1] !== '/') {
        return `${this.$util.apiURL()}/treatment/dosage/print/${treatment.selectedDosage?.id}/${this.attendance.id}/${
          treatment.selectedDosage?.key === 'firstPrescription'
        }/${this.selectedMedicinePrescriptionTemplateId}`
      } else {
        return `${this.$util.apiURL()}treatment/dosage/print/${treatment.selectedDosage?.id}/${this.attendance.id}/${
          treatment.selectedDosage?.key === 'firstPrescription'
        }/${this.selectedMedicinePrescriptionTemplateId}`
      }
    },
    openPrintExamPrescriptionModal(treatment) {
      this.currentTreatment = treatment
      this.examModal = true
    },
    getTreatmentDosagesOptions(dosages) {
      let result = []
      dosages = dosages.filter((d) => {
        const patientAge = this.$util.toAgeNumber(this.attendancePatient.birthday)
        const patientWeight = this.attendancePatient.currentWeightKg

        return (
          (d.minAge || 0) <= patientAge &&
          (d.maxAge || Infinity) >= patientAge &&
          (d.minWeight || 0) <= patientWeight &&
          (d.maxWeight || Infinity) >= patientWeight
        )
      })
      result.push(
        ...dosages.map((d) => ({
          key: 'firstPrescription',
          text: `Primeira prescrição | ${this.getRangeText(d.minAge, d.maxAge, 'anos')} | ${this.getRangeText(
            d.minWeight,
            d.maxWeight,
            'kg',
          )}`,
          value: d.firstPrescription,
          id: d.id,
          lme: d.treatmentDosageLme,
        })),
      )
      result.push(
        ...dosages.map((d) => ({
          key: 'maintenancePrescription',
          text: `Manutenção | ${this.getRangeText(d.minAge, d.maxAge, 'anos')} | ${this.getRangeText(
            d.minWeight,
            d.maxWeight,
            'kg',
          )}`,
          value: d.maintenancePrescription,
          id: d.id,
          lme: d.treatmentDosageLme,
        })),
      )

      return result
    },
    getDefaultData() {
      return {
        selectedTreatments: [],
        customPrescriptions: [],
        medicines: {
          selected: [],
          other: null,
        },
      }
    },
    addCustomPrescription() {
      if (!this.phaseData.customPrescriptions) {
        this.$set(this.phaseData, 'customPrescriptions', [])
      }

      this.phaseData.customPrescriptions.push('')
    },
    removeCustomPrescription(index) {
      this.phaseData.customPrescriptions.splice(index, 1)
    },
    getCustomPrescriptionUrl(index) {
      return `${this.$util.apiURL()}/treatment/custom-prescription/print/${this.attendance.id}/${
        this.selectedMedicinePrescriptionTemplateId
      }/${index}`
    },
    async loadData() {
      try {
        this.loading = true
        this.treatments = (await this.$api.getTreatmentsForDisease(this.attendanceDiseaseId)).items
          .map((t) => {
            let result = { ...t }
            if (t.dosages[0]?.firstPrescription) {
              result.selectedDosage = {
                key: 'firstPrescription',
                value: t.dosages[0]?.firstPrescription,
                id: t.dosages[0]?.id,
                lme: t.dosages[0]?.treatmentDosageLme,
              }
            }
            return result
          })
          .filter((t) =>
            t.dosages.some((d) => {
              const patientAge = this.$util.toAgeNumber(this.attendancePatient.birthday)
              const patientWeight = this.attendancePatient.currentWeightKg
              return (
                (d.minAge || 0) <= patientAge &&
                (d.maxAge || Infinity) >= patientAge &&
                (d.minWeight || 0) <= patientWeight &&
                (d.maxWeight || Infinity) >= patientWeight
              )
            }),
          )

        this.treatmentsCopy = JSON.parse(JSON.stringify(this.treatments))

        this.templates = (await this.$api.getTemplates()).items

        if (this.medicinePrescriptionTemplates.length > 0) {
          this.selectedMedicinePrescriptionTemplateId = this.medicinePrescriptionTemplates[0].id
        }
        if (this.examPrescriptionTemplates.length > 0) {
          this.selectedExamPrescriptionTemplateId = this.examPrescriptionTemplates[0].id
        }

        this.phaseData.selectedTreatments = [
          ...this.phaseData.selectedTreatments.map((t) => ({
            ...this.treatments.find((t2) => t2.id === t.id),
            selectedDosage: t.selectedDosage,
          })),
        ]

        this.error = false
      } catch (error) {
        this.$root.error(error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    filterTreatmentList(event) {
      if (event.length < 3) {
        this.treatments = this.treatmentsCopy
        return
      }
      this.treatments = this.treatmentsCopy.filter((t) => t.medicine.name.toLowerCase().includes(event.toLowerCase()))
    },
    onBlurFilterTreatmentList(event) {
      const value = event.target.value
      if (value.length === 0) {
        this.showAllTreatments = false
      }
    },
    async updatePatientHeight() {
      this.$store.commit('attendance/setAttendanceHeightCm', this.phaseData.heightCm)
    },
    async updatePatientWeight() {
      this.$store.commit('attendance/setAttendanceWeightKg', this.phaseData.weightKg)
    },
  },
}
</script>

<style scoped>
.header {
  height: 64px;
}

.title-text {
  color: #2b2b2b;
}

.content {
  color: #808080;
  line-height: 20px;
  margin-left: 36px;
}

.bg-active {
  background-color: #e0f0ff !important;
}

.bg-inactive {
  background-color: #ffffff !important;
  opacity: 1 !important;
}

.v-btn.v-item--active::before {
  background-color: #ffffff;
}

#hypothesis :deep(.textarea--readonly) {
  padding: 0;
}
</style>
